import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import LightGallery from "lightgallery/react";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";

import Breadcrumb from "./widget/Breadcrumb";
import FooterThree from "./widget/FooterThree";
import HeaderOne from "./widget/HeaderOne";
import Animation from "../helper/Animation";
import RootLayout from "./layout";

const API_URL = process.env.REACT_APP_API_URL || "https://api.npskudlu.com/api";

const ActivityDetails = () => {
  const { id } = useParams();
  const [activity, setActivity] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/v1/get-activity${id ? `?id=${id}` : ""}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );

      const payload = response.data || {};
      let act = null;

      // Normalize payload structure
      if (Array.isArray(payload.data) && payload.data.length > 0) {
        act =
          payload.data.find((it) => it._id === id || it.id === id) ||
          payload.data[0];
      } else if (payload.data && typeof payload.data === "object") {
        act = payload.data;
      } else if (payload && typeof payload === "object") {
        act = payload;
      }

      if (!act) {
        setError("Activity not found.");
        setActivity(null);
        setImages([]);
        return;
      }

      setActivity(act);

      // --- Normalize image data like GallerySection ---
      let imgs = [];

      if (Array.isArray(act.image)) {
        imgs = act.image.flatMap((imgObj) => {
          if (!imgObj) return [];
          if (typeof imgObj === "string") return [imgObj];
          if (imgObj.mediaLink) return [imgObj.mediaLink];
          if (imgObj.url) return [imgObj.url];
          return [];
        });
      } else if (typeof act.image === "string") {
        imgs = [act.image];
      } else if (
        act.image &&
        typeof act.image === "object" &&
        (act.image.mediaLink || act.image.url)
      ) {
        imgs = [act.image.mediaLink || act.image.url];
      }

      setImages(imgs.filter(Boolean));
    } catch (err) {
      console.error("Failed to fetch activity:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load activity.",
      );
      setActivity(null);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <RootLayout>
      <Animation />
      <HeaderOne />
      <Breadcrumb title="Activity" />

      <main>
        <section className="py-40">
          <div className="container">
            <Link to="/activities" className="text-main-600 fw-semibold">
              &larr; Back to Activities
            </Link>

            {loading && <p className="mt-16">Loading activity details...</p>}
            {error && <p className="mt-16 text-red-600">{error}</p>}
            {!loading && !error && !activity && (
              <p className="mt-16">No activity found.</p>
            )}

            {!loading && activity && (
              <>
                <div className="mt-24">
                  <h4 className="mb-8">{activity.name || activity.title}</h4>
                  {activity.description && (
                    <p className="mt-12 mb-5">{activity.description}</p>
                  )}
                </div>
                <br />

                <div className="container">
                  <div className="masonry">
                    {images.length > 0 && (
                      <LightGallery speed={500} plugins={[lgThumbnail, lgZoom]}>
                        {images.map((src, idx) => (
                          <a
                            key={idx}
                            className="masonry__item position-relative rounded-12 overflow-hidden"
                            href={src}
                            style={{ display: "block" }}
                          >
                            <img
                              src={src}
                              alt={`${activity.name || "Activity"} ${idx + 1}`}
                              style={{
                                width: "100%",
                                height: "auto",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          </a>
                        ))}
                      </LightGallery>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </main>

      <FooterThree />
    </RootLayout>
  );
};

export default ActivityDetails;
